import styles from "./HeroSection.module.css";
import { CardElement } from "@stripe/react-stripe-js";
import { useProtestPaymentHero } from "@/features/protest/viewModal/protestPaymentHero";
import { Modal } from "@/shared/modal";

export const HeroSection = () => {
  const {
    isProcessing,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    useReferralPoints,
    setUseReferralPoints,
    availableReferralPoints,
    payableAmount,
    onStartProtest,

    onConfirmCardPayment,
    minPayableAmount,
    couponCodeInput,
    setCouponCodeInput,
    appliedCoupon,
    couponError,
    isApplyingCoupon,
    onApplyCoupon,
    onRemoveCoupon,
    subtotalAfterReferral,
  } = useProtestPaymentHero();

  const showMinChargeNotice =
    payableAmount === minPayableAmount && (useReferralPoints || appliedCoupon != null);
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          <h1 className={styles.title}>Protest Fees</h1>
          <p className={styles.subtitle}>
            Understand the costs involved in your property tax protest.
          </p>

          <div className={styles.tagContainer}>
            <div className={styles.performanceTag}>
              <div className={styles.tagIcon}></div>
              <span>PERFORMANCE FOCUSED</span>
            </div>
          </div>

          <h2 className={styles.heading}>
            Our model is built on <span className={styles.highlight}>Pure Success.</span>
          </h2>

          <div className={styles.contextInfo}>
            <p className={styles.contextGreen}>In Texas, property taxes reset every year.</p>
            <p className={styles.contextGray}>
              To help keep your tax burden from increasing, a new protest is needed each year.
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          <div className={styles.rightStack}>
            <div className={styles.cardWrapper}>
              {/* Overlay Savings Share Graphic */}
              <div className={styles.savingsGraphic}>
                <div className={styles.savingsCircle}>
                  <div className={styles.savingsPercentage}>70%</div>
                  <div className={styles.savingsLabel}>SAVINGS SHARE</div>
                  <div className={styles.savingsDescription}>
                    We share in the savings only when we successfully lower your property value.
                  </div>
                </div>
              </div>

              <div className={styles.pricingCard}>
                <div className={styles.priceRow}>
                  <span className={styles.price}>${payableAmount}</span>
                  <span className={styles.priceNote}>Payable Now</span>
                </div>
                <h3 className={styles.serviceTitle}>Annual property and community monitoring</h3>
                <div className={styles.serviceDivider} />
                <p className={styles.serviceDescription}>
                  We monitor your property value and surrounding community data throughout the year
                  so you are prepared when it is time to protest your property taxes.
                </p>
              </div>
            </div>

            <div className={styles.actionRow}>
              <div className={styles.discountsColumn}>
                {availableReferralPoints > 0 && (
                  <div className={styles.referralPointsCard}>
                    <button
                      type="button"
                      className={styles.referralPointsHeader}
                      onClick={() => setUseReferralPoints((prev) => !prev)}
                      disabled={isProcessing || appliedCoupon != null}
                    >
                      <span className={styles.checkboxContainer}>
                        <span
                          className={`${styles.checkbox} ${useReferralPoints ? styles.checkboxChecked : ""}`}
                        >
                          {useReferralPoints ? "✓" : ""}
                        </span>
                      </span>

                      <span className={styles.referralPointsIcon} aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path d="M20 8h-2.18A2.99 2.99 0 0 0 18 7a3 3 0 1 0-6-2 3 3 0 1 0-6 2c0 .35.06.69.18 1H4a2 2 0 0 0-2 2v3h2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7h2v-3a2 2 0 0 0-2-2Zm-7-2a1 1 0 1 1 2 0c0 .55-.45 1-1 1h-1V6Zm-4 0a1 1 0 1 1 2 0v1H10c-.55 0-1-.45-1-1Zm9 14H6v-7h12v7Zm2-9H4v-1h16v1Z" />
                        </svg>
                      </span>

                      <span className={styles.referralPointsContent}>
                        <span className={styles.referralPointsTitle}>Use Referral Rewards</span>
                        <span className={styles.referralPointsSubtitle}>
                          You have {availableReferralPoints} points available
                        </span>
                        {appliedCoupon != null && (
                          <span className={styles.exclusionHint}>
                            Remove your promo code to use referral rewards.
                          </span>
                        )}
                      </span>
                    </button>
                    {useReferralPoints && (
                      <p className={styles.exclusionHint}>
                        Promo codes can&apos;t be combined with referral rewards.
                      </p>
                    )}
                  </div>
                )}

                {!useReferralPoints && (
                  <div className={styles.couponCard}>
                    <div className={styles.couponCardHeader}>
                      <span className={styles.couponCardTitle}>Promo code</span>
                      {appliedCoupon && (
                        <button
                          type="button"
                          className={styles.couponRemove}
                          onClick={onRemoveCoupon}
                          disabled={isProcessing || isApplyingCoupon}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className={styles.couponRow}>
                      <input
                        type="text"
                        className={styles.couponInput}
                        value={couponCodeInput}
                        onChange={(e) => setCouponCodeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void onApplyCoupon();
                          }
                        }}
                        placeholder="Enter code"
                        disabled={isProcessing || isApplyingCoupon || appliedCoupon != null}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Promo code"
                      />
                      <button
                        type="button"
                        className={styles.couponApplyButton}
                        onClick={() => void onApplyCoupon()}
                        disabled={
                          isProcessing ||
                          isApplyingCoupon ||
                          appliedCoupon != null ||
                          !couponCodeInput.trim()
                        }
                      >
                        {isApplyingCoupon ? "Applying…" : "Apply"}
                      </button>
                    </div>
                    {couponError && <p className={styles.couponError}>{couponError}</p>}
                    {appliedCoupon && appliedCoupon.discountAmount > 0 && (
                      <p className={styles.couponSuccess}>
                        ${appliedCoupon.discountAmount} off applied (order total $
                        {subtotalAfterReferral} → ${payableAmount})
                      </p>
                    )}
                    {appliedCoupon &&
                      appliedCoupon.discountAmount <= 0 &&
                      appliedCoupon.finalAmount != null && (
                        <p className={styles.couponSuccess}>
                          Promo applied — you pay ${payableAmount} now.
                        </p>
                      )}
                  </div>
                )}

                <div className={styles.bottomSection}>
                  <button
                    className={styles.ctaButton}
                    disabled={isProcessing}
                    onClick={() => void onStartProtest()}
                  >
                    {isProcessing ? "Creating Payment..." : "Start My Protest"}
                  </button>
                </div>

                {showMinChargeNotice && (
                  <div className={styles.minChargeNotice}>
                    <p className={styles.checkPayabilityTextBold}>
                      Minimum $1.00 charge required for recurring subscription activation.
                    </p>
                    <p className={styles.checkPayabilityText}>
                      Credits and promo codes may reduce the fee but cannot replace the required
                      payment method authorization.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        title="Enter Card Details"
        titleClassName={styles.modalTitle}
        headerClassName={styles.modalHeader}
        unconstrained
        className={styles.modalCard}
        bodyClassName={styles.modalCardBody}
        closeButtonDisabled={isProcessing}
        ariaLabel="Card payment"
      >
        <p className={styles.modalSubtitle}>Card number, expiry date, and CVC</p>
        <div className={styles.cardElementWrap}>
          <CardElement />
        </div>

        <button
          className={styles.modalPayButton}
          disabled={isProcessing}
          onClick={() => void onConfirmCardPayment()}
        >
          {isProcessing ? "Processing..." : `Pay $${payableAmount}`}
        </button>
      </Modal>
    </div>
  );
};
